import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue
} from 'framer-motion';
import {
  TrendingUp,
  Shield,
  Layers,
  ChevronRight,
  ArrowUpRight,
  Percent,
  Briefcase,
  Building,
  User,
  DollarSign,
  Activity,
  Compass,
  Award,
  Clock,
  BarChart2,
  X,
  Check,
  Sliders,
  Info,
  Menu,
  Phone,
  Mail,
  MapPin,
  Lock,
  Zap,
  Cpu,
  Globe,
  Database,
  ArrowRight,
  Eye,
  TrendingDown,
  Wifi,
  Battery,
  Send,
  MessageSquare,
  ChevronDown,
  MousePointerClick
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TradingViewMarketDashboard } from './market-section';
import Header from '../header';
import Footer from '../Footer';
import config from '../../config';
import ReCAPTCHA from "react-google-recaptcha";


const PARTNERS = [
  "UTI", "Nippon India", "Edelweiss", "Motilal Oswal",
  "Parag Parikh", "HDFC", "SBI", "ICICI Prudential",
  "Quant", "Aditya Birla", "Invesco"
];

const SERVICES = [
  {
    id: 'mutual-funds',
    title: 'Mutual Funds',
    shortDesc: 'Curated high-yield and balanced asset classes orchestrated by Nerus algorithms.',
    icon: Layers,
    accent: 'from-blue-600 via-cyan-400 to-indigo-500',
    details: 'Access institutional-grade mutual funds matching your risk appetite. Dynamic rebalancing and low expense ratios ensure your capital works seamlessly to capture market momentum.',
    projectedYield: '14.8% APY',
    riskLevel: 'Moderate to High',
    liquidity: 'T+2 Business Days'
  },
  {
    id: 'insurance',
    title: 'Insurances',
    shortDesc: 'Generational risk mitigation through customized premium Term, Health, Life & Asset coverage.',
    icon: Shield,
    accent: 'from-emerald-500 via-teal-400 to-green-600',
    details: 'Protect your legacy with high-sum assured plans, global medical cover, and robust estate planning strategies customized to shield multi-generational wealth from volatile events.',
    projectedYield: 'Guaranteed Shield',
    riskLevel: 'Ultra-low / Hedged',
    liquidity: 'Custom / Maturing'
  },
  {
    id: 'sip',
    title: 'Systematic Investment Plans (SIP)',
    shortDesc: 'Disciplined capital accumulation engine leveraging compounding algorithms.',
    icon: TrendingUp,
    accent: 'from-purple-500 via-pink-400 to-indigo-600',
    details: 'Automated weekly or monthly micro-allocations into hyper-targeted portfolios. Harness cost averaging and continuous compounding with zero manual friction.',
    projectedYield: '15.6% APY',
    riskLevel: 'Dynamic / Averaged',
    liquidity: 'Flexible'
  },
  {
    id: 'swp',
    title: 'Systematic Withdrawal Plans (SWP)',
    shortDesc: 'Structured tax-efficient cash flow generators for stable post-career transitions.',
    icon: ArrowUpRight,
    accent: 'from-amber-500 via-orange-400 to-yellow-600',
    details: 'Transform your accumulated nest egg into a predictable, highly tax-optimized monthly income stream while keeping your remaining principal invested and growing.',
    projectedYield: '9.0% Withdrawal Base',
    riskLevel: 'Conservative Growth',
    liquidity: 'Monthly Automatic'
  },
  {
    id: 'fixed-deposit',
    title: 'Fixed Deposits',
    shortDesc: 'Guaranteed-yield fortress instruments with elite institution-backed security.',
    icon: Percent,
    accent: 'from-rose-500 via-pink-500 to-amber-500',
    details: 'Lock in industry-leading fixed yields with premium corporate and bank partners. Absolute liquidity options paired with iron-clad capital preservation guarantees.',
    projectedYield: '8.2% Compound',
    riskLevel: 'Sovereign Backed',
    liquidity: 'Locked Options'
  },
  {
    id: 'trading',
    title: 'Trading Terminal',
    shortDesc: 'Ultra-low latency execution across global equities, commodities, and options.',
    icon: Activity,
    accent: 'from-violet-500 via-fuchsia-400 to-cyan-500',
    details: 'Equipped with real-time risk parameters, advanced charting, margin multipliers, and direct-market access for active individual and institutional traders.',
    projectedYield: 'Variable Alpha',
    riskLevel: 'High / Tactical',
    liquidity: 'Instantaneous'
  },
  {
    id: 'pms-individual',
    title: 'Individual PMS',
    shortDesc: 'Tailor-made concentrated equity portfolios for high-net-worth individuals.',
    icon: User,
    accent: 'from-yellow-500 via-amber-500 to-orange-500',
    details: 'Bespoke investment mandates targeting absolute, non-correlated returns. Direct access to your dedicated Chief Investment Officer (CIO) and private reports.',
    projectedYield: '18.4% APY',
    riskLevel: 'High Active Return',
    liquidity: 'Quarterly Check'
  },
  {
    id: 'pms-company',
    title: 'Corporate PMS & Treasury',
    shortDesc: 'Sophisticated liquidity orchestration & strategic capital growth for enterprises.',
    icon: Building,
    accent: 'from-cyan-500 via-blue-600 to-purple-600',
    details: 'Treasury optimization maximizing short-term yields while retaining robust operating liquidity. Customized risk boundaries aligned with corporate governance rules.',
    projectedYield: '11.5% APY',
    riskLevel: 'Enterprise Preserved',
    liquidity: 'Dynamic / Multi-tier'
  }
];

const INJECTED_STYLES = `
  @keyframes scanline {
    0% { transform: translateY(-100%); }
    100% { transform: translateY(100%); }
  }
  @keyframes textShimmer {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes ripple {
    0% { transform: scale(1); opacity: 0.4; }
    100% { transform: scale(2.2); opacity: 0; }
  }
  .animate-scanline {
    animation: scanline 6s linear infinite;
  }
  .animate-shimmer {
    background-size: 200% auto;
    animation: textShimmer 4s linear infinite;
  }
  .custom-grain {
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.015'/%3E%3C/svg%3E");
  }
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .animate-ripple {
    animation: ripple 2s cubic-bezier(0, 0, 0.2, 1) infinite;
  }
  @keyframes marquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(calc(-50% - 10px)); }
  }
  .animate-marquee {
    display: flex;
    width: max-content;
    animation: marquee 30s linear infinite;
  }
  .animate-marquee:hover {
    animation-play-state: paused;
  }
`;

export default function FrameHome() {
  // Check if loading screen was shown in the last 24 hours
  const [isLoading, setIsLoading] = useState(() => {
    const lastLoadTime = localStorage.getItem('nerusLastLoadTime');
    const now = Date.now();
    if (lastLoadTime && now - parseInt(lastLoadTime) < 24 * 60 * 60 * 1000) {
      return false; // Skip loading if shown within 24 hours
    }
    localStorage.setItem('nerusLastLoadTime', now.toString());
    return true; // Show loading on first visit or after 24 hours
  });
  const [loaderProgress, setLoaderProgress] = useState(0);
  const [loaderText, setLoaderText] = useState('Initializing Nerus Core Subsystems...');
  const [activeTab, setActiveTab] = useState('sip');
  const [selectedService, setSelectedService] = useState(SERVICES[0]);
  const [userProfile, setUserProfile] = useState('individual');
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardAnswers, setWizardAnswers] = useState({});
  const [wizardResult, setWizardResult] = useState(null);
  const [advisorConsultMode, setAdvisorConsultMode] = useState(false);
  const [isSubmittingAdvisor, setIsSubmittingAdvisor] = useState(false);
  const [advisorSubmitted, setAdvisorSubmitted] = useState(false);
  const [isSubmittingWizard, setIsSubmittingWizard] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [wizardError, setWizardError] = useState(null);

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    if (wizardError) setWizardError(null);
  };

  const submitWizardRequest = async () => {
    if (!captchaToken) return;
    setIsSubmittingWizard(true);
    setWizardError(null);
    try {
      const payload = {
        name: wizardAnswers.name || '',
        mobile: wizardAnswers.mobile || '',
        email: wizardAnswers.email || '',
        timestamp: new Date().toISOString(),
        recaptcha_token: captchaToken
      };

      // Execute the API request natively (public endpoint)
      const response = await fetch(`${config.API_URL}/requests/`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setWizardStep(3);
      } else {
        console.error('Failed to submit request');
        setWizardError('Failed to submit request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      setWizardError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmittingWizard(false);
    }
  };

  const [currentTime, setCurrentTime] = useState('09:41');
  const [batteryLevel, setBatteryLevel] = useState(98);
  const [signalStrength, setSignalStrength] = useState(4);
  const [iphoneLoading, setIphoneLoading] = useState(false);

  // Smart chat simulator state
  const [chatMessages, setChatMessages] = useState([
    { id: 'msg-init', sender: 'system', text: 'Secure Channel Alpha-9 Online. Nerus Quantum Transmissions are active.', time: '09:41' }
  ]);
  const [showPortals, setShowPortals] = useState(false);
  const [isChatTyping, setIsChatTyping] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [hasTriggeredQuery, setHasTriggeredQuery] = useState(false);
  const [showHandHint, setShowHandHint] = useState(() => {
    const lastHintTime = localStorage.getItem('nerusLastHandHintTime');
    const now = Date.now();
    if (lastHintTime && now - parseInt(lastHintTime) < 24 * 60 * 60 * 1000) {
      return false; // Skip if shown within 24 hours
    }
    return true; // Show on first visit or after 24 hours
  });

  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);
  const springConfig = { damping: 40, stiffness: 250, mass: 0.5 };
  const cursorXSpring = useSpring(cursorX, springConfig);
  const cursorYSpring = useSpring(cursorY, springConfig);

  const heroX = useMotionValue(0);
  const heroY = useMotionValue(0);
  const heroRotateX = useSpring(useTransform(heroY, [-300, 300], [12, -12]), springConfig);
  const heroRotateY = useSpring(useTransform(heroX, [-300, 300], [-12, 12]), springConfig);

  // SIP Calculator State
  const [sipMonthly, setSipMonthly] = useState(50000);
  const [sipRate, setSipRate] = useState(15);
  const [sipYears, setSipYears] = useState(15);

  // SWP Calculator State
  const [swpPrincipal, setSwpPrincipal] = useState(10000000);
  const [swpWithdrawal, setSwpWithdrawal] = useState(60000);
  const [swpRate, setSwpRate] = useState(9);
  const [swpYears, setSwpYears] = useState(15);

  // FD Calculator State
  const [fdPrincipal, setFdPrincipal] = useState(2500000);
  const [fdRate, setFdRate] = useState(8.2);
  const [fdYears, setFdYears] = useState(7);

  // Portfolio Builder State
  const [riskTolerance, setRiskTolerance] = useState(75);
  const [portfolioSize, setPortfolioSize] = useState(10000000);

  const { scrollY } = useScroll();
  const yParallaxBg = useTransform(scrollY, [0, 1000], [0, 350]);
  const yParallaxMid = useTransform(scrollY, [0, 1000], [0, -180]);
  const yParallaxStars = useTransform(scrollY, [0, 1000], [0, 120]);

  const canvasRef = useRef(null);
  const telemetrySectionRef = useRef(null);
  const chatEndRef = useRef(null);

  // Auto-triggering live chat simulation when scrolling to Telemetry Console
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggeredQuery) {
          triggerQuantumQuery();
        }
      },
      { threshold: 0.25 }
    );
    if (telemetrySectionRef.current) {
      observer.observe(telemetrySectionRef.current);
    }
    return () => observer.disconnect();
  }, [hasTriggeredQuery]);

  // Keep chat scrolled to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isChatTyping, showPortals]);

  const triggerQuantumQuery = () => {
    if (hasTriggeredQuery) return;
    setHasTriggeredQuery(true);

    // 1. Send simulated user message
    setTimeout(() => {
      setChatMessages(prev => [
        ...prev,
        { id: 'msg-user-1', sender: 'user', text: 'What are the services offered by NERUS?', time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }
      ]);

      // 2. Trigger typing indicator
      setIsChatTyping(true);

      // 3. Deliver decrypted response and activate portals
      setTimeout(() => {
        setIsChatTyping(false);
        setChatMessages(prev => [
          ...prev,
          {
            id: 'msg-system-reply',
            sender: 'system',
            text: 'Accessing secure nodes. Decrypting Nerus services list. Below are the premium portal nodes ready for integration. Select any of them to load detailed risk parameters:',
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }
        ]);
        setShowPortals(true);
      }, 1800);
    }, 800);
  };

  const handleCustomChatMessage = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    const userText = chatInput;
    setChatInput('');

    // Append user message
    setChatMessages(prev => [
      ...prev,
      { id: Date.now().toString(), sender: 'user', text: userText, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }
    ]);

    setIsChatTyping(true);

    setTimeout(() => {
      setIsChatTyping(false);
      let reply = "Secure ledger transmission logged. Nerus systems are calibrating. Tap the 'Get started' action node above to initialize your direct strategy protocols.";

      if (userText.toLowerCase().includes('service') || userText.toLowerCase().includes('offer') || userText.toLowerCase().includes('product')) {
        reply = "Active services updated. Direct portals unlocked inside this secure link. Select any node on your panel for instant structural metrics.";
        setShowPortals(true);
      }

      setChatMessages(prev => [
        ...prev,
        { id: (Date.now() + 1).toString(), sender: 'system', text: reply, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }
      ]);
    }, 1400);
  };

  useEffect(() => {
    if (!isLoading) return;
    const loaderPhrases = [
      'Establishing Nerus Quantum Encryption Protocols...',
      'Mapping Multi-Asset Node Telemetry...',
      'Synchronizing Global Index Flux Nodes...',
      'Calibrating Risk Matrices & Algorithmic Engines...',
      'Securing Inter-bank Liquidity Gateways...',
      'NERUS Decryption Complete. Initiating System Experience.'
    ];

    const startTime = Date.now();
    const duration = 9000; // 9 seconds

    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / duration) * 100, 100);

      setLoaderProgress(Math.floor(progress));

      if (progress >= 100) {
        clearInterval(progressInterval);
        setTimeout(() => setIsLoading(false), 600);
        return;
      }

      const phraseIdx = Math.min(Math.floor((progress / 100) * loaderPhrases.length), loaderPhrases.length - 1);
      setLoaderText(loaderPhrases[phraseIdx]);
    }, 50);

    return () => clearInterval(progressInterval);
  }, [isLoading]);

  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      cursorX.set(e.clientX - 16);
      cursorY.set(e.clientY - 16);
    };
    window.addEventListener('mousemove', handleGlobalMouseMove);

    const clockInterval = setInterval(() => {
      const now = new Date();
      const hrs = String(now.getHours()).padStart(2, '0');
      const mins = String(now.getMinutes()).padStart(2, '0');
      setCurrentTime(`${hrs}:${mins}`);
    }, 1000);

    const tickerInterval = setInterval(() => {
      // Fluctuating signals/battery inside simulated device
      setSignalStrength(prev => Math.min(4, Math.max(2, prev + (Math.random() > 0.7 ? (Math.random() > 0.5 ? 1 : -1) : 0))));
      setBatteryLevel(prev => Math.max(10, prev - (Math.random() > 0.95 ? 1 : 0)));
    }, 2500);

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      let animationFrameId;
      let width = (canvas.width = window.innerWidth);
      let height = (canvas.height = window.innerHeight);

      const handleResize = () => {
        width = canvas.width = window.innerWidth;
        height = canvas.height = window.innerHeight;
      };
      window.addEventListener('resize', handleResize);

      class Particle {
        constructor() {
          this.reset();
        }
        reset() {
          this.x = Math.random() * width;
          this.y = Math.random() * height;
          this.vx = (Math.random() - 0.5) * 0.25;
          this.vy = (Math.random() - 0.5) * 0.25;
          this.radius = Math.random() * 2 + 0.5;
          this.color = Math.random() > 0.88 ? 'rgba(59, 130, 246, 0.4)' : 'rgba(14, 116, 144, 0.2)';
        }
        update() {
          this.x += this.vx;
          this.y += this.vy;
          if (this.x < 0 || this.x > width || this.y < 0 || this.y > height) {
            this.reset();
          }
        }
        draw() {
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
          ctx.fillStyle = this.color;
          ctx.fill();
        }
      }

      const particles = Array.from({ length: 140 }, () => new Particle());

      const render = () => {
        ctx.fillStyle = 'rgba(1, 3, 14, 0.12)';
        ctx.fillRect(0, 0, width, height);

        particles.forEach(p => {
          p.update();
          p.draw();
        });

        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const dx = particles[i].x - particles[j].x;
            const dy = particles[i].y - particles[j].y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 140) {
              ctx.beginPath();
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.strokeStyle = `rgba(14, 165, 233, ${(1 - dist / 140) * 0.08})`;
              ctx.lineWidth = 0.55;
              ctx.stroke();
            }
          }
        }
        animationFrameId = requestAnimationFrame(render);
      };

      render();

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        cancelAnimationFrame(animationFrameId);
        clearInterval(tickerInterval);
        clearInterval(clockInterval);
      };
    }
  }, []);

  const handleIphoneSelectService = (service) => {
    if (showHandHint) {
      setShowHandHint(false);
      localStorage.setItem('nerusLastHandHintTime', Date.now().toString());
    }
    setIphoneLoading(true);
    setTimeout(() => {
      setSelectedService(service);
      setIphoneLoading(false);
    }, 300);
  };

  const handleDropdownItemClick = (type, serviceId) => {
    if (type) {
      setActiveTab(type);
      const calcSection = document.getElementById('calculators');
      calcSection?.scrollIntoView({ behavior: 'smooth' });
    }
    if (serviceId) {
      const match = SERVICES.find(s => s.id === serviceId);
      if (match) {
        setSelectedService(match);
        const teleSection = document.getElementById('telemetry-console');
        teleSection?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const sipResult = useMemo(() => {
    const monthlyRate = sipRate / 12 / 100;
    const months = sipYears * 12;
    const invested = sipMonthly * months;
    const total = sipMonthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
    const gains = total - invested;

    const chartData = [];
    for (let yr = 0; yr <= sipYears; yr++) {
      const yrMonths = yr * 12;
      const yrInvested = sipMonthly * yrMonths;
      const yrTotal = yr === 0 ? 0 : sipMonthly * ((Math.pow(1 + monthlyRate, yrMonths) - 1) / monthlyRate) * (1 + monthlyRate);
      chartData.push({
        name: `Yr ${yr}`,
        Invested: Math.round(yrInvested),
        Wealth: Math.round(yrTotal)
      });
    }
    return { invested: Math.round(invested), total: Math.round(total), gains: Math.round(gains), chartData };
  }, [sipMonthly, sipRate, sipYears]);

  const swpResult = useMemo(() => {
    const monthlyRate = swpRate / 12 / 100;
    const months = swpYears * 12;
    let balance = swpPrincipal;
    let totalWithdrawn = 0;
    const chartData = [{ name: 'Start', Balance: swpPrincipal, Withdrawn: 0 }];

    for (let yr = 1; yr <= swpYears; yr++) {
      for (let m = 0; m < 12; m++) {
        balance = balance * (1 + monthlyRate) - swpWithdrawal;
        totalWithdrawn += swpWithdrawal;
        if (balance < 0) balance = 0;
      }
      chartData.push({
        name: `Yr ${yr}`,
        Balance: Math.round(balance),
        Withdrawn: Math.round(totalWithdrawn)
      });
    }
    return { finalBalance: Math.round(balance), totalWithdrawn: Math.round(totalWithdrawn), chartData };
  }, [swpPrincipal, swpWithdrawal, swpRate, swpYears]);

  const fdResult = useMemo(() => {
    const total = fdPrincipal * Math.pow(1 + fdRate / 100, fdYears);
    const gains = total - fdPrincipal;

    const chartData = [];
    for (let yr = 0; yr <= fdYears; yr++) {
      const yrTotal = fdPrincipal * Math.pow(1 + fdRate / 100, yr);
      chartData.push({
        name: `Yr ${yr}`,
        Principal: Math.round(fdPrincipal),
        Growth: Math.round(yrTotal)
      });
    }
    return { total: Math.round(total), gains: Math.round(gains), chartData };
  }, [fdPrincipal, fdRate, fdYears]);

  const customPortfolio = useMemo(() => {
    const risk = riskTolerance / 100;
    const isCorp = userProfile === 'corporate';
    let equity, debt, alternative, gold, corporateBond;

    if (isCorp) {
      equity = Math.round(risk * 35);
      corporateBond = Math.round((1 - risk) * 45);
      debt = Math.round((1 - risk) * 30);
      alternative = Math.round(risk * 20);
      gold = 100 - (equity + corporateBond + debt + alternative);
    } else {
      equity = Math.round(risk * 70);
      debt = Math.round((1 - risk) * 20);
      alternative = Math.round(risk * 15);
      gold = Math.round((1 - risk) * 5 + 5);
      const sum = equity + debt + alternative + gold;
      if (sum !== 100) {
        equity += (100 - sum);
      }
    }

    const data = [
      { name: 'Elite Equities', value: equity, color: '#0EA5E9', desc: 'Direct compounders & index alpha tools' },
      { name: 'Sovereign Debt', value: debt, color: '#10B981', desc: 'AAA secure corporate & state bonds' },
      { name: 'Precious Hedges', value: gold, color: '#F59E0B', desc: 'Sovereign gold & system shield hedges' },
      { name: 'Venture & Alternatives', value: alternative, color: '#8B5CF6', desc: 'Market-neutral pre-IPO global assets' }
    ];

    if (isCorp) {
      data.push({ name: 'Enterprise Liquidity Engine', value: corporateBond, color: '#EC4899', desc: 'Ultra-low-duration liquid vaults' });
    }
    return data.filter(d => d.value > 0);
  }, [riskTolerance, userProfile]);

  const startWizard = () => {
    setWizardStep(1);
    setWizardAnswers({});
    setWizardResult(null);
    const element = document.getElementById('wizard-section');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAnswer = (key, val) => {
    const updated = { ...wizardAnswers, [key]: val };
    setWizardAnswers(updated);

    if (key === 'timeframe') {
      let recommendation = '';
      let assetMix = '';
      const risk = updated.risk;
      const timeframe = val;

      if (risk === 'high' && timeframe === 'long') {
        recommendation = 'Aggressive Alpha Equities & PMS Strategy';
        assetMix = '75% Equities | 20% Alternatives | 5% Hedges';
      } else if (risk === 'low' || timeframe === 'short') {
        recommendation = 'Fortress Shield Fixed Deposits & Government Debt';
        assetMix = '15% Index Funds | 65% Sovereign Debt | 20% FDs';
      } else {
        recommendation = 'Compounding Core Hybrid Portfolio Plan';
        assetMix = '55% Equities | 35% Bonds | 10% Gold';
      }

      setWizardResult({ recommendation, assetMix });
      setWizardStep(4);
    } else {
      setWizardStep(prev => prev + 1);
    }
  };

  const submitAdvisorRequest = (e) => {
    e.preventDefault();
    setIsSubmittingAdvisor(true);
    setTimeout(() => {
      setIsSubmittingAdvisor(false);
      setAdvisorSubmitted(true);
    }, 1500);
  };

  const heroContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.2
      }
    }
  };

  const heroItemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", damping: 22, stiffness: 110 }
    }
  };

  return (
    <div className="min-h-screen bg-[#010411] text-slate-100 font-sans selection:bg-cyan-500 selection:text-black overflow-x-hidden relative custom-grain">
      <style>{INJECTED_STYLES}</style>

      {/* High Cinematic Glowing Nodes Background */}
      <div className="absolute top-0 inset-x-0 h-[64rem] bg-gradient-to-b from-[#051139] via-[#01051c] to-[#010411] pointer-events-none z-0" />

      {/* Interactive Cursor Trail */}
      <motion.div
        style={{ x: cursorXSpring, y: cursorYSpring }}
        className="hidden md:block fixed top-0 left-0 w-8 h-8 rounded-full border border-cyan-400/60 bg-cyan-400/5 pointer-events-none z-50 shadow-[0_0_20px_rgba(14,165,233,0.3)]"
      />

      <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />

      {/* Cinematic Screen-Entry Loader */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.55, ease: "easeInOut" } }}
            className="fixed inset-0 z-[100] bg-[#01030e] flex flex-col items-center justify-center p-6 select-none"
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_70%_at_50%_50%,#09153a_0%,#01030e_100%)] opacity-80" />
            <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(14,165,233,0.03)_1px,transparent_1px)] bg-[size:100%_4px] pointer-events-none">
              <div className="w-full h-1/2 bg-gradient-to-b from-cyan-500/10 to-transparent absolute top-0 left-0 animate-scanline" />
            </div>

            <div className="relative flex flex-col items-center space-y-12 max-w-lg w-full text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                className="relative"
              >
                <div className="w-24 h-24 rounded-3xl border border-cyan-500/20 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-2xl border-2 border-dashed border-cyan-400/40 flex items-center justify-center animate-spin" style={{ animationDuration: '6s' }} />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <img src="https://res.cloudinary.com/dvqwan8qc/image/upload/v1769418002/nerusfull_tdovvh.jpg" alt="Nerus Logo" className="h-10 w-auto object-contain rounded-sm" />
                </div>
              </motion.div>

              <div className="space-y-4 w-full">
                <h3 className="text-white font-black text-2xl tracking-[0.18em] uppercase">NERUS QUANTUM LEDGER</h3>
                <div className="flex justify-between text-[11px] font-mono text-cyan-400/80 px-1">
                  <span className="flex items-center"><Cpu className="w-3.5 h-3.5 mr-1" /> CORE.SYSTEM_OK</span>
                  <span className="flex items-center"><Globe className="w-3.5 h-3.5 mr-1" /> SECURE_LEDGER_ONLINE</span>
                </div>

                <div className="h-1.5 w-full bg-slate-900 rounded-full overflow-hidden border border-white/5 relative">
                  <motion.div
                    className="h-full bg-gradient-to-r from-cyan-500 via-blue-400 to-indigo-600"
                    initial={{ width: "0%" }}
                    animate={{ width: `${loaderProgress}%` }}
                    transition={{ duration: 0.9 }}
                  />
                </div>

                <div className="flex justify-between text-xs font-mono">
                  <span className="text-slate-400 animate-pulse">{loaderText}</span>
                  <span className="text-cyan-400 font-bold">{loaderProgress}%</span>
                </div>

                {/* Disclaimer Section */}
                <div className="mt-8 pt-6 border-t border-cyan-500/20 max-w-md text-left">
                  <p style={{ fontSize: '14px' }} className="text-slate-300 leading-relaxed space-y-4">
                    <span className="block mb-3"><strong>Mutual Fund Disclaimer:</strong> Mutual Fund investments are subject to market risks, read all scheme related documents carefully. The value of units may go up or down depending on the factors and forces affecting the securities laws and capital markets.</span>
                    <span className="block mb-3"><strong>Insurance Disclaimer:</strong> Insurance is a subject matter of solicitation. For more details on risk factors, terms, and conditions, please read the sales brochure carefully before concluding a sale.</span>
                    <span className="block"><strong>Interest Rate Disclaimer:</strong> Interest rates are subject to change at the discretion of the Bank/Institution. Premature withdrawal may attract a penalty and a reduction in interest rate. Deposits are insured up to the limit prescribed by the DICGC (or relevant local body).</span>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Glowing Parallax Background Filters */}
      <motion.div style={{ y: yParallaxBg }} className="fixed top-0 left-1/4 w-[700px] h-[700px] bg-blue-600/5 rounded-full filter blur-[140px] pointer-events-none z-0" />
      <motion.div style={{ y: yParallaxMid }} className="fixed top-1/3 right-1/4 w-[800px] h-[800px] bg-cyan-600/5 rounded-full filter blur-[160px] pointer-events-none z-0" />
      <motion.div style={{ y: yParallaxStars }} className="fixed bottom-1/4 left-1/3 w-[600px] h-[600px] bg-indigo-600/5 rounded-full filter blur-[130px] pointer-events-none z-0" />

      {/* Global Navigation Header */}
      <Header onStartWizard={startWizard} onDropdownItemClick={handleDropdownItemClick} />

      {/* Cinematic Hero Showcase Section */}
      <section className="relative pt-10 pb-20 lg:pt-20 lg:pb-28 overflow-hidden px-4 sm:px-6 lg:px-8 z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">

          <motion.div
            variants={heroContainerVariants}
            initial="hidden"
            animate="visible"
            className="lg:col-span-7 space-y-10 text-center lg:text-left"
          >
            <motion.div
              variants={heroItemVariants}
              className="inline-flex items-center space-x-2.5 px-4 py-2.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-[10px] font-black tracking-[0.25em] text-cyan-400 uppercase"
            >
              <Award className="w-4 h-4 text-cyan-400 animate-pulse" />
              <span>Sovereign Wealth Compounding Engine</span>
            </motion.div>

            <motion.h1
              variants={heroItemVariants}
              className="text-4xl sm:text-6.5xl md:text-7xl font-black text-white leading-[1.05] tracking-tight"
            >
              Sovereign Wealth, <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-200 to-indigo-500 animate-shimmer">Orchestrated by Nerus.</span>
            </motion.h1>

            <motion.p
              variants={heroItemVariants}
              className="text-slate-300 text-base sm:text-xl max-w-2xl mx-auto lg:mx-0 leading-relaxed font-normal"
            >
              Unlock the compounding architecture of elite family offices. Secure global Mutual Funds, Strategic Compounding (SIP), Tax-Optimized SWP Systems, and Enterprise Treasury engines.
            </motion.p>

            <motion.div
              variants={heroItemVariants}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-5 pt-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={startWizard}
                className="w-full sm:w-auto px-10 h-16 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white font-bold rounded-xl text-xs uppercase tracking-widest shadow-[0_8px_30px_rgba(14,165,233,0.3)] flex items-center justify-center space-x-3 group"
              >
                <span>Start Investing with Nerus</span>
                <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </motion.button>
              <motion.a
                whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.08)" }}
                whileTap={{ scale: 0.95 }}
                href="#telemetry-console"
                className="w-full sm:w-auto px-10 h-16 border border-white/10 bg-white/5 text-white font-black text-xs uppercase tracking-widest rounded-xl flex items-center justify-center space-x-2.5"
              >
                <span>Interactive Terminal</span>
              </motion.a>
            </motion.div>

            <motion.div
              variants={heroItemVariants}
              className="grid grid-cols-3 gap-8 pt-12 border-t border-white/5 max-w-xl mx-auto lg:mx-0 font-mono"
            >
              <div>
                <span className="block text-3xl font-black text-white">1Cr+</span>
                <span className="block text-[9px] text-slate-400 uppercase tracking-widest font-bold mt-1">Managed Capital</span>
              </div>
              <div>
                <span className="block text-3xl font-black text-emerald-400">99.82%</span>
                <span className="block text-[9px] text-slate-400 uppercase tracking-widest font-bold mt-1">Sovereign Retention</span>
              </div>
              {/* <div>
                <span className="block text-3xl font-black text-cyan-400">0.42ms</span>
                <span className="block text-[9px] text-slate-400 uppercase tracking-widest font-bold mt-1">Route Latency</span>
              </div> */}
            </motion.div>
          </motion.div>

          {/* Interactive Simulation Dashboard with Cinematic Glass */}
          <div className="lg:col-span-5 flex justify-center">
            <motion.div
              style={{ rotateX: heroRotateX, rotateY: heroRotateY, transformStyle: "preserve-3d" }}
              onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                heroX.set(e.clientX - rect.left - rect.width / 2);
                heroY.set(e.clientY - rect.top - rect.height / 2);
              }}
              onMouseLeave={() => {
                heroX.set(0);
                heroY.set(0);
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 80, damping: 15, delay: 0.4 }}
              className="w-full max-w-[430px] rounded-[32px] bg-gradient-to-b from-[#070e28] to-[#010411] p-8 border border-white/10 shadow-[0_25px_65px_rgba(0,0,0,0.85)] relative overflow-hidden group animate-none"
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_300px_at_50%_-30px,rgba(14,165,233,0.1),transparent)] pointer-events-none" />

              <div className="flex justify-between items-center mb-10">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-cyan-500/80" />
                </div>
                <div className="flex items-center space-x-1 px-3 py-1 bg-white/5 border border-white/10 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[9px] font-bold text-slate-400 tracking-wider uppercase">Live Stream</span>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Nerus Aggregate Ledger</p>
                    <h2 className="text-4xl font-black text-white tracking-tight mt-1">5048000</h2>
                  </div>
                  <TrendingUp className="w-8 h-8 text-cyan-400 bg-cyan-400/10 p-1.5 rounded-xl border border-cyan-400/20" />
                </div>

                <div className="pt-4 border-t border-white/5 space-y-3">
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-slate-400 flex items-center"><Database className="w-3.5 h-3.5 mr-1.5 text-cyan-400" /> Dynamic Allocations</span>
                    <span className="text-white font-bold">4+ Active Nodes</span>
                  </div>
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-slate-400 flex items-center"><Lock className="w-3.5 h-3.5 mr-1.5 text-indigo-400" /> Ledger Security</span>
                    <span className="text-emerald-400 font-bold">256-SHA Quantum</span>
                  </div>
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-slate-400 flex items-center"><Zap className="w-3.5 h-3.5 mr-1.5 text-yellow-400" /> Target Yield (APY)</span>
                    <span className="text-cyan-400 font-bold">+12.00%</span>
                  </div>
                </div>

                <div className="pt-6 relative">
                  <div className="h-20 w-full bg-slate-900/40 rounded-2xl border border-white/5 overflow-hidden flex items-end">
                    <div className="w-full h-full flex items-end justify-between px-1">
                      {[30, 45, 35, 60, 50, 80, 70, 95, 85].map((val, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ height: 0 }}
                          animate={{ height: `${val}%` }}
                          transition={{ duration: 1.2, delay: idx * 0.1, ease: "easeOut" }}
                          className="w-[8%] bg-gradient-to-t from-cyan-500/20 to-cyan-400 rounded-t-md"
                        />
                      ))}
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#010411] via-transparent to-transparent pointer-events-none" />
                </div>
              </div>
            </motion.div>
          </div>

        </div>
      </section>

      {/* STRATEGIC PARTNERS SHOWCASE SECTION (Replaced Nerus Market Feeds ticker) */}
      <section id="partners" className="border-y border-white/5 bg-[#01030e] py-7 relative z-10 overflow-hidden">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <div className="shrink-0 flex items-center space-x-3.5 border-r border-white/10 pr-6 mr-2">
              <Building className="w-6 h-6 sm:w-8 sm:h-8 text-cyan-400 animate-pulse shrink-0" />
              <div className="flex flex-col text-left leading-tight">
                <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 font-mono">Trusted By</span>
                <span className="text-xs sm:text-sm md:text-base font-black uppercase tracking-[0.1em] text-cyan-400 font-mono">Financial Institutions</span>
              </div>
            </div>

            <div className="flex-1 overflow-hidden py-2.5 w-full relative">
              {/* Blur mask left and right to make it smooth */}
              <div className="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-[#01030e] to-transparent z-20 pointer-events-none" />
              <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-[#01030e] to-transparent z-20 pointer-events-none" />

              <div className="animate-marquee space-x-5 flex">
                {/* Loop 1 */}
                {PARTNERS.map((partner, idx) => (
                  <div
                    key={`p1-${idx}`}
                    className="flex items-center space-x-3.5 bg-white/5 border border-white/5 rounded-2xl px-6 py-4 shrink-0 transition-all duration-300 hover:scale-105 hover:border-cyan-500/30 hover:bg-white/10"
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.8)]" />
                    <span className="text-xs sm:text-sm font-mono text-slate-200 font-extrabold uppercase tracking-wider">{partner}</span>
                  </div>
                ))}
                {/* Loop 2 */}
                {PARTNERS.map((partner, idx) => (
                  <div
                    key={`p2-${idx}`}
                    className="flex items-center space-x-3.5 bg-white/5 border border-white/5 rounded-2xl px-6 py-4 shrink-0 transition-all duration-300 hover:scale-105 hover:border-cyan-500/30 hover:bg-white/10"
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.8)]" />
                    <span className="text-xs sm:text-sm font-mono text-slate-200 font-extrabold uppercase tracking-wider">{partner}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE FEATURE: Sovereign Communicator & Dynamic Decrypting Portals */}
      <section ref={telemetrySectionRef} id="telemetry-console" className="py-24 sm:py-32 relative z-10 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#010411] via-[#030922] to-[#010411]">
        <div className="max-w-7xl mx-auto">

          <div className="text-center max-w-3xl mx-auto space-y-5 mb-20">
            <span className="text-xs font-black uppercase tracking-[0.3em] text-cyan-500">NERUS CONNECT</span>
            <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight">Financial Infrastructure Terminal</h2>
            <p className="text-slate-400 text-base sm:text-lg">
              Explore dynamic financial instruments directly. Watch the secure iPhone terminal request and decrypt real-time financial protocols below.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

            {/* LEFT COLUMN: Simulated iPhone Device with Conversational Telemetry Initiation */}
            <div className="lg:col-span-5 flex justify-center sticky top-28">
              <div className="relative w-full max-w-[340px] h-[640px] rounded-[52px] border-[10px] border-slate-855 bg-slate-950 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.95)] overflow-hidden outline outline-2 outline-slate-700/30">

                {/* iPhone Screen Reflection */}
                <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-white/5 to-transparent pointer-events-none z-30" />

                {/* Dynamic Island Notch */}
                <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-full z-45 flex items-center justify-between px-3 text-[10px] text-white/50">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-900 border border-slate-800" />
                  <span className="text-[7px] text-cyan-400 font-mono tracking-tighter">SECURE NODE</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                </div>

                {/* iPhone Status Bar */}
                <div className="absolute top-0 inset-x-0 h-10 px-6 pt-3.5 flex justify-between items-center text-[11px] font-bold text-slate-300 z-40 font-mono">
                  <span>{currentTime}</span>
                  <div className="flex items-center space-x-1.5">
                    <Wifi className="w-3.5 h-3.5 text-slate-300" />
                    <div className="flex items-center space-x-0.5">
                      {[...Array(4)].map((_, i) => (
                        <div
                          key={i}
                          className={`w-[2px] rounded-full bg-slate-300 ${signalStrength > i ? 'h-2.5' : 'h-1 opacity-30'}`}
                        />
                      ))}
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-[9px] font-semibold">{batteryLevel}%</span>
                      <div className="relative w-5 h-2.5 border border-slate-300 rounded-[3px] p-[1px] flex items-center">
                        <div
                          className="h-full bg-emerald-400 rounded-[1px] transition-all duration-1000"
                          style={{ width: `${batteryLevel}%` }}
                        />
                        <div className="absolute -right-[3px] top-1/2 -translate-y-1/2 w-[2px] h-1.5 bg-slate-300 rounded-r-sm" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Secure Chat Shell Content */}
                <div className="h-full pt-10 pb-4 flex flex-col justify-between bg-[radial-gradient(ellipse_at_top_right,rgba(11,21,57,0.9),rgba(1,3,14,1))] text-slate-100 relative">

                  {/* Chat Sub-Header */}
                  <div className="px-4 py-3 bg-[#050b20]/80 border-b border-white/5 backdrop-blur-md flex items-center justify-between z-10">
                    <div className="flex items-center space-x-2.5">
                      <div className="relative w-8 h-8 rounded-full bg-gradient-to-tr from-cyan-500 to-indigo-600 p-[1px]">
                        <div className="w-full h-full rounded-full bg-slate-950 flex items-center justify-center">
                          <span className="text-cyan-400 text-xs font-black">N</span>
                        </div>
                        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-slate-950 rounded-full animate-pulse" />
                      </div>
                      <div>
                        <h4 className="text-[11px] font-extrabold text-white leading-tight uppercase tracking-wider">Nerus Services</h4>
                        <span className="text-[9px] text-slate-400 flex items-center"><Lock className="w-2.5 h-2.5 mr-1 text-emerald-400" /> End-to-End Encryption</span>
                      </div>
                    </div>
                    <Sliders className="w-4 h-4 text-slate-400" />
                  </div>

                  {/* Messages Stream Container */}
                  <div className="flex-1 overflow-y-auto px-3.5 py-4 space-y-4 no-scrollbar">

                    {chatMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex items-start space-x-2 max-w-[85%] ${msg.sender === 'user' ? 'ml-auto justify-end flex-row-reverse space-x-reverse' : ''}`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${msg.sender === 'user' ? 'bg-cyan-500/20 border border-cyan-500/30' : 'bg-blue-600/20 border border-blue-500/30'}`}>
                          {msg.sender === 'user' ? <User className="w-3.5 h-3.5 text-cyan-400" /> : <MessageSquare className="w-3.5 h-3.5 text-blue-400" />}
                        </div>
                        <div className={`p-3 rounded-[18px] border border-white/5 shadow-md ${msg.sender === 'user' ? 'bg-[#0f1b31] rounded-tr-sm text-cyan-100' : 'bg-[#0b101f] rounded-tl-sm text-slate-300'}`}>
                          <p className="text-[11px] leading-relaxed font-sans">{msg.text}</p>
                          <span className="text-[8px] text-slate-500 block mt-1.5 text-right font-mono uppercase">
                            {msg.sender === 'user' ? 'SOVEREIGN USER' : 'NERUS GATEWAY'}
                          </span>
                        </div>
                      </div>
                    ))}

                    {/* Typing indicator */}
                    {isChatTyping && (
                      <div className="flex items-start space-x-2 max-w-[85%]">
                        <div className="w-6 h-6 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center shrink-0 mt-0.5">
                          <MessageSquare className="w-3.5 h-3.5 text-blue-400" />
                        </div>
                        <div className="bg-[#0b101f] border border-white/5 p-3 rounded-[18px] rounded-tl-sm shadow-md flex items-center space-x-1.5">
                          <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    )}

                    {/* Manual Trigger Fallback button */}
                    {!hasTriggeredQuery && (
                      <button
                        onClick={triggerQuantumQuery}
                        className="w-full bg-cyan-500/10 border border-dashed border-cyan-400/40 p-4 rounded-2xl text-center cursor-pointer group hover:bg-cyan-500/20 transition-all text-xs text-cyan-400 font-bold"
                      >
                        <Zap className="w-4 h-4 mx-auto mb-2 text-cyan-400 animate-pulse" />
                        Connect to Nerus
                      </button>
                    )}

                    {/* Decrypted Portals inside the secure Link */}
                    {showPortals && (
                      <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-2.5 pt-2 sm:max-h-64 relative"
                      >
                        <span className="text-[9px] font-black tracking-widest text-cyan-500/80 uppercase block px-1 font-mono">Decrypted Assets Matrix</span>

                        {SERVICES.map((serv) => {
                          const Icon = serv.icon;
                          const isChosen = selectedService?.id === serv.id;
                          return (
                            <motion.button
                              key={serv.id}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleIphoneSelectService(serv)}
                              className={`relative w-full text-left p-3 rounded-[20px] transition-all flex items-center justify-between border ${isChosen
                                ? 'bg-gradient-to-r from-cyan-400/10 to-indigo-600/5 border-cyan-400/50 shadow-[0_0_15px_rgba(14,165,233,0.15)]'
                                : 'bg-[#030716]/80 hover:bg-[#070e28]/90 border-white/5'
                                }`}
                            >
                              {showHandHint && serv.id === 'insurance' && (
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1, y: [0, -10, 0] }}
                                  transition={{ y: { repeat: Infinity, duration: 1.5, ease: "easeInOut" } }}
                                  className="absolute right-4 -bottom-12 z-50 pointer-events-none flex flex-col items-center"
                                >
                                  <span className="text-[10px] font-bold text-slate-900 bg-cyan-400 px-2 py-0.5 rounded-full mb-1 shadow-[0_0_10px_rgba(34,211,238,0.6)]">Tap here</span>
                                  <MousePointerClick className="w-6 h-6 text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
                                </motion.div>
                              )}
                              <div className="flex items-center space-x-3">
                                <div className={`w-8 h-8 rounded-xl bg-gradient-to-tr ${serv.accent} p-2 flex items-center justify-center`}>
                                  <Icon className="w-4 h-4 text-slate-950" />
                                </div>
                                <div>
                                  <h2 className={`text-sm font-extrabold transition-colors ${isChosen ? 'text-cyan-400' : 'text-slate-200'}`}>{serv.title}</h2>
                                  {/* <span className="text-[9px] text-slate-400 block mt-0.5 line-clamp-1 hidden sm:block">{serv.shortDesc}</span> */}
                                </div>
                              </div>
                              <div className="shrink-0 pl-2">
                                {isChosen ? (
                                  <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 block relative">
                                    <span className="absolute inset-0 rounded-full bg-cyan-400 animate-ripple" />
                                  </span>
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                                )}
                              </div>
                            </motion.button>
                          );
                        })}
                      </motion.div>
                    )}

                    <div ref={chatEndRef} />
                  </div>

                  {/* Simulated Messenger Controls */}
                  <form onSubmit={handleCustomChatMessage} className="px-3.5 pt-2 pb-1 border-t border-white/5 bg-[#01030e]/95 backdrop-blur-md flex items-center justify-between space-x-2">
                    <div className="flex-1 h-9 bg-slate-900/90 border border-white/5 rounded-full px-4 flex items-center justify-between">
                      <input
                        type="text"
                        placeholder="Secure direct system query..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        className="bg-transparent text-[10px] text-slate-300 placeholder-slate-500 focus:outline-none w-full mr-2"
                      />
                      <Lock className="w-3 h-3 text-emerald-400/70" />
                    </div>
                    <button
                      type="submit"
                      className="w-9 h-9 rounded-full bg-gradient-to-tr from-cyan-500 to-indigo-600 flex items-center justify-center hover:shadow-[0_0_10px_rgba(14,165,233,0.4)] transition-all shrink-0"
                    >
                      <Send className="w-3.5 h-3.5 text-white" />
                    </button>
                  </form>

                </div>

              </div>
            </div>

            {/* RIGHT COLUMN: Interactive Holographic Decrypted Terminal */}
            <div className="lg:col-span-7">
              <AnimatePresence mode="wait">
                {selectedService && (
                  <motion.div
                    key={selectedService.id}
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ type: "spring", damping: 18, stiffness: 100 }}
                    className="bg-[#05091e]/40 border border-white/10 rounded-[32px] p-6 sm:p-8 space-y-8 relative overflow-hidden shadow-2xl min-h-[580px] flex flex-col justify-between animate-none"
                  >

                    {/* Animated visual grid lines */}
                    <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(14,165,233,0.015)_1px,transparent_1px)] bg-[size:100%_8px] pointer-events-none" />
                    <div className="absolute top-0 right-0 w-80 h-80 bg-[radial-gradient(circle_300px_at_100%_0%,rgba(14,165,233,0.08),transparent)] pointer-events-none" />

                    <div>
                      {/* Terminal Status Banner */}
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-white/5 pb-6 gap-4">
                        <div className="flex items-center space-x-4">
                          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-tr ${selectedService.accent} p-3.5 flex items-center justify-center shadow-lg`}>
                            {React.createElement(selectedService.icon, { className: "w-7 h-7 text-slate-950" })}
                          </div>
                          <div>
                            <span className="text-[10px] font-mono tracking-[0.3em] text-cyan-400 block uppercase">DECRYPTED TELEMETRY MATRIX</span>
                            <h3 className="text-2xl sm:text-3xl font-black text-white mt-1">{selectedService.title}</h3>
                          </div>
                        </div>

                        <div className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-xl flex items-center space-x-2">
                          <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                          <span className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-wider">ACTIVE PROTOCOL</span>
                        </div>
                      </div>

                      {/* Decrypted Payload Content */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8">
                        <div className="space-y-5">
                          <span className="text-xs font-black uppercase tracking-widest text-slate-400 block">CORE DESCRIPTION</span>
                          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                            {selectedService.details}
                          </p>
                          <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                            {selectedService.shortDesc}
                          </p>
                        </div>

                        {/* Telemetry Teleports */}
                        <div className="space-y-6 bg-[#04081c]/50 border border-white/5 rounded-2xl p-6 relative">
                          <span className="text-xs font-black uppercase tracking-widest text-slate-400 block border-b border-white/5 pb-3">STRUCTURAL RISK PARAMETERS</span>

                          <div className="space-y-4">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-slate-400 flex items-center"><TrendingUp className="w-4 h-4 mr-1.5 text-cyan-500" /> Projected Yield</span>
                              <span className="text-white font-mono font-bold text-sm">{selectedService.projectedYield}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-slate-400 flex items-center"><Shield className="w-4 h-4 mr-1.5 text-blue-500" /> Volatility Matrix</span>
                              <span className="text-cyan-400 font-mono font-bold text-sm">{selectedService.riskLevel}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-slate-400 flex items-center"><Activity className="w-4 h-4 mr-1.5 text-indigo-500" /> Redemption Cycle</span>
                              <span className="text-slate-200 font-mono font-bold text-sm">{selectedService.liquidity}</span>
                            </div>
                          </div>

                          {/* <div className="pt-4 border-t border-white/5 flex items-center justify-between text-[10px] font-mono text-slate-500">
                            <span>DEC_SEC_KEY: nerus_82a1_99</span>
                            <span>NODE_VERIFY: OK</span>
                          </div> */}
                        </div>
                      </div>
                    </div>

                    {/* Quick Simulation Trigger Buttons */}
                    <div className="pt-8 border-t border-white/5 flex flex-col gap-4">
                      <div className="flex items-center space-x-3 text-xs text-slate-400">
                        <Info className="w-4 h-4 text-cyan-400 shrink-0" />
                        <span>Tap any interactive nodes on the phone to switch decrypted displays instantly.</span>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 w-full">
                        <a
                          href="#calculators"
                          className="flex-1 text-center px-4 py-3 border border-white/10 hover:border-cyan-400 text-white hover:text-cyan-400 text-xs uppercase tracking-widest font-black rounded-xl transition-all"
                        >
                          Simulate compounding
                        </a>
                        <button
                          onClick={startWizard}
                          className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white text-xs uppercase tracking-widest font-black rounded-xl hover:shadow-[0_0_20px_rgba(14,165,233,0.3)] transition-all"
                        >
                          Start Investment
                        </button>
                      </div>
                    </div>

                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </div>

        </div>
      </section>

      {/* Capital Accumulation Modeler Section */}
      <section id="calculators" className="py-24 sm:py-32 relative z-10 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto space-y-5 mb-20">
            <span className="text-xs font-black uppercase tracking-[0.3em] text-cyan-500">Nerus Simulator</span>
            <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight">Capital Accumulation Modeler</h2>
            <p className="text-slate-400 text-base sm:text-lg">
              Perform multi-variable compounding simulations using algorithmic projections.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">

            {/* CALCULATOR CONTROLS */}
            <div className="lg:col-span-5 bg-slate-900/20 border border-white/5 rounded-[32px] p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <div className="grid grid-cols-3 gap-2.5 p-1.5 bg-[#04081c] rounded-2xl border border-white/5 mb-8">
                  {['sip', 'swp', 'fd'].map((type) => (
                    <button
                      key={type}
                      onClick={() => setActiveTab(type)}
                      className={`py-3 text-xs font-black uppercase tracking-wider rounded-xl transition-all ${activeTab === type ? 'bg-cyan-400 text-black shadow-lg' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
                    >
                      {type === 'sip' ? 'SIP' : type === 'swp' ? 'SWP' : 'FD'}
                    </button>
                  ))}
                </div>

                {activeTab === 'sip' && (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300 font-bold">Monthly Strategy</span>
                        <span className="text-cyan-400 font-black">₹{sipMonthly.toLocaleString()}</span>
                      </div>
                      <input
                        type="range" min="5000" max="500000" step="5000" value={sipMonthly}
                        onChange={(e) => setSipMonthly(Number(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300 font-bold">Expected Growth (p.a.)</span>
                        <span className="text-cyan-400 font-black">{sipRate}%</span>
                      </div>
                      <input
                        type="range" min="5" max="30" step="0.5" value={sipRate}
                        onChange={(e) => setSipRate(Number(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300 font-bold">Accumulation Horizon</span>
                        <span className="text-cyan-400 font-black">{sipYears} Years</span>
                      </div>
                      <input
                        type="range" min="1" max="40" step="1" value={sipYears}
                        onChange={(e) => setSipYears(Number(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                  </div>
                )}

                {activeTab === 'swp' && (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300 font-bold">Initial Principal</span>
                        <span className="text-cyan-400 font-black">${swpPrincipal.toLocaleString()}</span>
                      </div>
                      <input
                        type="range" min="1000000" max="100000000" step="500000" value={swpPrincipal}
                        onChange={(e) => setSwpPrincipal(Number(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300 font-bold">Monthly Payout Target</span>
                        <span className="text-cyan-400 font-black">₹{swpWithdrawal.toLocaleString()}</span>
                      </div>
                      <input
                        type="range" min="10000" max="1000000" step="5000" value={swpWithdrawal}
                        onChange={(e) => setSwpWithdrawal(Number(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300 font-bold">Strategic Rate (p.a.)</span>
                        <span className="text-cyan-400 font-black">{swpRate}%</span>
                      </div>
                      <input
                        type="range" min="4" max="20" step="0.5" value={swpRate}
                        onChange={(e) => setSwpRate(Number(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300 font-bold">Withdrawal Tenure</span>
                        <span className="text-cyan-400 font-black">{swpYears} Years</span>
                      </div>
                      <input
                        type="range" min="1" max="30" step="1" value={swpYears}
                        onChange={(e) => setSwpYears(Number(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                  </div>
                )}

                {activeTab === 'fd' && (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300 font-bold">FD Principal</span>
                        <span className="text-cyan-400 font-black">₹{fdPrincipal.toLocaleString()}</span>
                      </div>
                      <input
                        type="range" min="50000" max="50000000" step="50000" value={fdPrincipal}
                        onChange={(e) => setFdPrincipal(Number(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300 font-bold">Fixed Yield (p.a.)</span>
                        <span className="text-cyan-400 font-black">{fdRate}%</span>
                      </div>
                      <input
                        type="range" min="3" max="15" step="0.1" value={fdRate}
                        onChange={(e) => setFdRate(Number(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300 font-bold">Locked Horizon</span>
                        <span className="text-cyan-400 font-black">{fdYears} Years</span>
                      </div>
                      <input
                        type="range" min="1" max="15" step="1" value={fdYears}
                        onChange={(e) => setFdYears(Number(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* DYNAMIC METRIC CARDS */}
              <div className="pt-8 border-t border-white/5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {activeTab === 'sip' && (
                    <>
                      <div className="bg-[#030616] p-4 rounded-2xl border border-white/5">
                        <span className="text-[10px] text-slate-400 font-bold block font-mono">Invested Balance</span>
                        <span className="text-base font-black text-white mt-1 block">₹{sipResult.invested.toLocaleString()}</span>
                      </div>
                      <div className="bg-[#030616] p-4 rounded-2xl border border-white/5">
                        <span className="text-[10px] text-slate-400 font-bold block font-mono">Estimated Gains</span>
                        <span className="text-base font-black text-emerald-400 mt-1 block">₹{sipResult.gains.toLocaleString()}</span>
                      </div>
                    </>
                  )}
                  {activeTab === 'swp' && (
                    <>
                      <div className="bg-[#030616] p-4 rounded-2xl border border-white/5">
                        <span className="text-[10px] text-slate-400 font-bold block font-mono">Total Withdrawn</span>
                        <span className="text-base font-black text-cyan-400 mt-1 block">₹{swpResult.totalWithdrawn.toLocaleString()}</span>
                      </div>
                      <div className="bg-[#030616] p-4 rounded-2xl border border-white/5">
                        <span className="text-[10px] text-slate-400 font-bold block font-mono">Final Balance</span>
                        <span className="text-base font-black text-white mt-1 block">₹{swpResult.finalBalance.toLocaleString()}</span>
                      </div>
                    </>
                  )}
                  {activeTab === 'fd' && (
                    <>
                      <div className="bg-[#030616] p-4 rounded-2xl border border-white/5">
                        <span className="text-[10px] text-slate-400 font-bold block font-mono">Capital Invested</span>
                        <span className="text-base font-black text-white mt-1 block">₹{fdPrincipal.toLocaleString()}</span>
                      </div>
                      <div className="bg-[#030616] p-4 rounded-2xl border border-white/5">
                        <span className="text-[10px] text-slate-400 font-bold block font-mono">Compounded Return</span>
                        <span className="text-base font-black text-emerald-400 mt-1 block">₹{fdResult.gains.toLocaleString()}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* VISUAL CHART MODEL */}
            <div className="lg:col-span-7 bg-[#05091e]/40 border border-white/5 rounded-[32px] p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg font-black text-white">Compound Projection Curve</h3>
                    <p className="text-xs text-slate-400 mt-1">Simulated valuation trajectories mapped over time</p>
                  </div>
                  <div className="px-3.5 py-1.5 bg-cyan-400/10 border border-cyan-400/20 rounded-xl">
                    <span className="text-xs font-black text-cyan-400 uppercase tracking-wider font-mono">
                      {activeTab === 'sip' ? `Total: ₹${sipResult.total.toLocaleString()}` : activeTab === 'swp' ? `Final: ₹${swpResult.finalBalance.toLocaleString()}` : `Maturity: ₹${fdResult.total.toLocaleString()}`}
                    </span>
                  </div>
                </div>

                <div className="h-80 w-full mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={activeTab === 'sip' ? sipResult.chartData : activeTab === 'swp' ? swpResult.chartData : fdResult.chartData}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorBase" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#4338CA" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#4338CA" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="name" stroke="#475569" fontSize={11} tickLine={false} />
                      <YAxis stroke="#475569" fontSize={11} tickLine={false} tickFormatter={(val) => `₹${(val / 1000).toFixed(0)}k`} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#04081c', borderColor: '#1e293b', borderRadius: '12px' }}
                        labelStyle={{ color: '#94a3b8', fontWeight: 'bold' }}
                      />
                      {activeTab === 'sip' && (
                        <>
                          <Area type="monotone" dataKey="Invested" stroke="#4338CA" strokeWidth={2} fillOpacity={1} fill="url(#colorBase)" />
                          <Area type="monotone" dataKey="Wealth" stroke="#0EA5E9" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                        </>
                      )}
                      {activeTab === 'swp' && (
                        <>
                          <Area type="monotone" dataKey="Withdrawn" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorBase)" />
                          <Area type="monotone" dataKey="Balance" stroke="#0EA5E9" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                        </>
                      )}
                      {activeTab === 'fd' && (
                        <>
                          <Area type="monotone" dataKey="Principal" stroke="#6366F1" strokeWidth={2} fillOpacity={1} fill="url(#colorBase)" />
                          <Area type="monotone" dataKey="Growth" stroke="#0EA5E9" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                        </>
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row justify-between items-center bg-[#04081c] p-4 rounded-2xl border border-white/5">
                <div className="flex items-center space-x-3 text-xs text-slate-400 mb-3 sm:mb-0">
                  <Info className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span>Interactive outputs assume daily rebalancing systems. Actual dynamic results might fluctuate.</span>
                </div>
                {/* <button 
                  onClick={startWizard}
                  className="px-5 py-2.5 bg-white/5 border border-white/10 hover:border-cyan-400 text-white hover:text-cyan-400 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all"
                >
                  Verify Model
                </button> */}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* TradingView Market Dashboard */}
      <TradingViewMarketDashboard />

      {/* Systemic Wealth Diagnostic Audit */}
      <section id="wizard-section" className="py-24 sm:py-32 relative z-10 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-4xl mx-auto bg-slate-900/10 border border-white/5 rounded-[32px] p-8 sm:p-12 relative overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.6)]">

          <div className="absolute inset-0 bg-[radial-gradient(circle_500px_at_50%_-50px,rgba(14,165,233,0.04),transparent)] pointer-events-none" />

          <AnimatePresence mode="wait">
            {wizardStep === 0 && (
              <motion.div
                key="step-0" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }}
                className="text-center space-y-6 relative"
              >
                <div className="w-16 h-16 rounded-2xl bg-cyan-400/10 border border-cyan-400/20 text-cyan-400 p-4 mx-auto flex items-center justify-center">
                  <Compass className="w-8 h-8 animate-spin" style={{ animationDuration: '8s' }} />
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl sm:text-3.5xl font-black text-white tracking-tight">Systemic Wealth Diagnostic Audit</h3>
                  <p className="text-slate-400 text-sm sm:text-base max-w-xl mx-auto">
                    Evaluate wealth variables. Boot diagnostic nodes to analyze potential yield models aligned with risk limits.
                  </p>
                </div>
                <button
                  onClick={() => setWizardStep(1)}
                  className="px-8 py-4 bg-cyan-400 text-black font-black text-xs uppercase tracking-widest rounded-xl hover:shadow-[0_5px_20px_rgba(14,165,233,0.3)] transition-all"
                >
                  Boot Diagnostic
                </button>
              </motion.div>
            )}

            {wizardStep === 1 && (
              <motion.div
                key="step-1" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }}
                className="space-y-6"
              >
                <div className="flex justify-between items-center text-xs font-mono text-slate-400">
                  <span>Profile Selection</span>
                  <span>Step 1 of 2</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-black text-white">Select Your Profile Type</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { id: 'individual', label: 'Individual Portfolio', desc: 'Personal wealth growth and legacy compounding' },
                    { id: 'corporate', label: 'Corporate / Business Entity', desc: 'Business treasury and institutional allocation' }
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => { setUserProfile(opt.id); setWizardStep(2); }}
                      className="p-6 rounded-2xl border border-white/5 bg-white/5 hover:border-cyan-400 text-left hover:bg-cyan-400/5 transition-all group"
                    >
                      <span className="block font-bold text-white group-hover:text-cyan-400 text-sm mb-1">{opt.label}</span>
                      <span className="text-xs text-slate-400 leading-normal block">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {wizardStep === 2 && (
              <motion.div
                key="step-2" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }}
                className="space-y-6"
              >
                <div className="flex justify-between items-center text-xs font-mono text-slate-400">
                  <span>Details Collection</span>
                  <span>Step 2 of 2</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-black text-white">Share Your Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-black uppercase tracking-wider text-slate-400 block mb-2">Full Name / Company Name</label>
                    <input
                      type="text"
                      placeholder={userProfile === 'individual' ? 'Your Full Name' : 'Company Name'}
                      value={wizardAnswers.name || ''}
                      onChange={(e) => setWizardAnswers(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 focus:bg-cyan-400/5 transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black uppercase tracking-wider text-slate-400 block mb-2">Email Address</label>
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={wizardAnswers.email || ''}
                      onChange={(e) => setWizardAnswers(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 focus:bg-cyan-400/5 transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black uppercase tracking-wider text-slate-400 block mb-2">Phone Number</label>
                    <input
                      type="tel"
                      placeholder="+1 (800) XXXX-XXXX"
                      value={wizardAnswers.mobile || ''}
                      onChange={(e) => setWizardAnswers(prev => ({ ...prev, mobile: e.target.value }))}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 focus:bg-cyan-400/5 transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black uppercase tracking-wider text-slate-400 block mb-2">Inquiry Subject</label>
                    <textarea
                      placeholder="Tell us about your investment goals and requirements..."
                      rows="4"
                      value={wizardAnswers.subject || ''}
                      onChange={(e) => setWizardAnswers(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 focus:bg-cyan-400/5 transition-all text-sm resize-none"
                    />
                  </div>
                </div>

                {wizardError && (
                  <div className="text-red-400 bg-red-400/10 border border-red-400/20 p-3 rounded-xl text-xs font-bold text-center">
                    {wizardError}
                  </div>
                )}

                <div className="pt-2 flex justify-center">
                  <ReCAPTCHA
                    sitekey={config.RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    theme="dark"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    onClick={() => setWizardStep(0)}
                    className="flex-1 px-6 py-3 border border-white/10 bg-white/5 text-slate-300 hover:text-white rounded-xl text-xs uppercase tracking-widest font-bold transition-all"
                  >
                    Back
                  </button>
                  <button
                    onClick={submitWizardRequest}
                    disabled={isSubmittingWizard || !captchaToken}
                    className="flex-1 px-8 py-3 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white font-bold rounded-xl text-xs uppercase tracking-widest shadow-lg hover:shadow-[0_0_20px_rgba(14,165,233,0.3)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingWizard ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </motion.div>
            )}

            {wizardStep === 3 && (
              <motion.div
                key="step-3" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }}
                className="space-y-8 text-center"
              >
                <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 p-3 mx-auto flex items-center justify-center">
                  <Check className="w-8 h-8" />
                </div>

                <div className="space-y-3">
                  <span className="text-xs font-black uppercase tracking-[0.2em] text-emerald-400">Request Submitted</span>
                  <h3 className="text-2xl sm:text-3.5xl font-black text-white tracking-tight">Thank You for Your Request</h3>
                  <p className="text-slate-400 text-sm max-w-lg mx-auto leading-relaxed">
                    Our wealth management team will review your request and contact you within 24 hours to discuss your investment requirements.
                  </p>
                </div>

                <div className="bg-[#04081c] border border-white/5 p-6 rounded-2xl max-w-lg mx-auto">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Profile Type</span>
                  <span className="text-base font-black text-cyan-400 mt-2 block capitalize">{userProfile === 'individual' ? 'Individual Portfolio' : 'Corporate Entity'}</span>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                  <button
                    onClick={() => setWizardStep(0)}
                    className="w-full sm:w-auto px-6 py-3.5 border border-white/10 bg-white/5 text-slate-300 hover:text-white rounded-xl text-xs uppercase tracking-widest font-bold transition-all"
                  >
                    Close
                  </button>
                  {/* <button
                    onClick={() => { setWizardStep(0); setAdvisorConsultMode(true); }}
                    className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white font-bold rounded-xl text-xs uppercase tracking-widest shadow-lg hover:shadow-[0_0_20px_rgba(14,165,233,0.3)] transition-all"
                  >
                    Chat with Advisor
                  </button> */}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </section>

      {/* Global Footer */}
      <Footer />

      {/* Get started Systemic Form Modal */}
      <AnimatePresence>
        {advisorConsultMode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#01030e]/95 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-[#05091e] border border-white/10 w-full max-w-lg rounded-[32px] p-8 relative overflow-hidden shadow-2xl"
            >
              <div className="absolute top-6 right-6">
                <button
                  onClick={() => { setAdvisorConsultMode(false); setAdvisorSubmitted(false); }}
                  className="p-2 bg-white/5 border border-white/10 rounded-xl text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!advisorSubmitted ? (
                <form onSubmit={submitAdvisorRequest} className="space-y-6">
                  <div className="space-y-1">
                    <h3 className="text-xl sm:text-2xl font-black text-white">Initialize Protocol</h3>
                    <p className="text-xs text-slate-400 leading-normal">
                      Initiate secure channel with Nerus private office investment committee.
                    </p>
                  </div>

                  <div className="space-y-4 pt-4 border-t border-white/5">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Sovereign Signature Name</label>
                      <input
                        type="text" required placeholder="Signature Name"
                        className="w-full bg-[#030616] border border-white/5 rounded-xl px-4 py-3.5 text-xs text-white focus:outline-none focus:border-cyan-400"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Secure Direct Mailbox</label>
                      <input
                        type="email" required placeholder="mailbox@domain.com"
                        className="w-full bg-[#030616] border border-white/5 rounded-xl px-4 py-3.5 text-xs text-white focus:outline-none focus:border-cyan-400"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Allocated Strategy Scope</label>
                      <select className="w-full bg-[#030616] border border-white/5 rounded-xl px-4 py-3.5 text-xs text-slate-400 focus:outline-none focus:border-cyan-400">
                        <option>Direct Legacy Wealth Accumulation (Individual)</option>
                        <option>Enterprise Liquidity Orchestration (Corporate)</option>
                        <option>Tax-Efficient Core Post-Career (SWP)</option>
                      </select>
                    </div>
                  </div>

                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={isSubmittingAdvisor}
                      className="w-full py-4 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white font-bold text-xs uppercase tracking-widest rounded-xl hover:shadow-lg transition-all flex items-center justify-center space-x-2"
                    >
                      {isSubmittingAdvisor ? (
                        <span>Configuring Safe Channel...</span>
                      ) : (
                        <>
                          <span>Establish Channel</span>
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center space-y-6 py-6"
                >
                  <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 p-3 mx-auto flex items-center justify-center">
                    <Check className="w-8 h-8" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl sm:text-2xl font-black text-white">Channel Live</h3>
                    <p className="text-xs text-slate-400 max-w-sm mx-auto leading-relaxed">
                      Your diagnostic parameters have been logged. A dedicated Private Wealth Director will contact your mailbox through a quantum-secure routing portal.
                    </p>
                  </div>
                  <button
                    onClick={() => { setAdvisorConsultMode(false); setAdvisorSubmitted(false); }}
                    className="px-6 py-3 border border-white/10 text-xs text-slate-300 rounded-xl uppercase font-black tracking-widest"
                  >
                    Close Protocol
                  </button>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}